// The search page's data against the public API: one request per filter
// change for the list and its facets, one in parallel for the dealer's content
// pages when there is a free text, plus one unfiltered request for the panel's
// full value lists and slider domains.
// Returns the same tuple as the legacy useSearch so Search/index.tsx can
// switch between the two.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MarketCode } from '../@types/MarketCode';
import { RequestState } from '../@types/RequestState';
import { Cars, CMS, Facet, SearchIndex } from '../Components/Search/SearchModels';
import { SearchType } from '../Components/Search/useSearch';
import { toContentIndex, toInitialFacets, toSearchIndex } from './adapter';
import { gqlQuery, PublicApiConfig } from './client';
import {
  buildSiteSearchQuery,
  FACETS_QUERY,
  multiSelectFacetAlias,
  SITE_CONTENT_QUERY,
  SITE_SEARCH_QUERY,
} from './queries';
import { FREE_TEXT, LIST_FILTERS, pageSize, toSearchVariables } from './searchParams';
import { AdsResult, FacetsData, Facet as GqlFacet, SiteContentData, SiteSearchData } from './types';

/** The facets of the filtered result, with every selected list filter's facet counted without its own filter. */
const withMultiSelectFacets = (
  data: SiteSearchData,
  ads: AdsResult,
  selected: string[]
): AdsResult => {
  if (!ads.facets || !selected.length) return ads;
  const facets = ads.facets.map((facet) => {
    if (!selected.includes(facet.field)) return facet;
    const alias = data.search?.[multiSelectFacetAlias(facet.field)] as
      | { facets?: GqlFacet[] | null }
      | undefined;
    return alias?.facets?.find((f) => f.field === facet.field) || facet;
  });
  return { ...ads, facets };
};

export interface SiteSearchConfig {
  api: PublicApiConfig;
  branchGroupId: string;
  market: MarketCode;
}

export interface SiteSearchResult {
  carFacets?: Facet[];
  cars?: SearchIndex<Cars>;
  retailers_cms?: SearchIndex<CMS>;
}

const useSiteSearch = (
  params: URLSearchParams,
  config: SiteSearchConfig,
  disabled?: boolean
): [
  SiteSearchResult | undefined,
  RequestState,
  ((searchType?: SearchType) => void) | undefined,
] => {
  const [unfiltered, setUnfiltered] = useState<AdsResult>();
  const [cars, setCars] = useState<SearchIndex<Cars>>();
  const [content, setContent] = useState<SearchIndex<CMS>>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const requestIndex = useRef(0);

  const paramsKey = params.toString();
  const q = params.get(FREE_TEXT)?.trim() || '';

  const baseVariables = useCallback(
    (page?: number) => ({
      ...toSearchVariables(params, config.market, { page }),
      branchGroupId: config.branchGroupId,
    }),
    [paramsKey, config.market, config.branchGroupId]
  );

  useEffect(() => {
    if (disabled) return;
    gqlQuery<FacetsData>(config.api, FACETS_QUERY, {
      market: [config.market],
      branchGroupId: config.branchGroupId,
    })
      .then((data) => {
        if (data.search?.ads) setUnfiltered(data.search.ads);
      })
      .catch(() => setIsError(true));
  }, [disabled, config.branchGroupId]);

  useEffect(() => {
    if (disabled) return;
    const index = ++requestIndex.current;
    setIsLoading(true);
    setIsError(false);
    setIsSuccess(false);
    const selected = LIST_FILTERS.filter((field) => params.getAll(field).length > 0);
    gqlQuery<SiteSearchData>(config.api, buildSiteSearchQuery(selected), {
      ...baseVariables(1),
      withFacets: true,
    })
      .then((data) => {
        if (index !== requestIndex.current) return;
        const ads = data.search?.ads;
        setCars(
          ads ? toSearchIndex(withMultiSelectFacets(data, ads, selected), params) : undefined
        );
        setIsSuccess(true);
      })
      .catch(() => {
        if (index === requestIndex.current) setIsError(true);
      })
      .finally(() => {
        if (index === requestIndex.current) setIsLoading(false);
      });

    // Content pages: only with a free text, and never in the way of the cars.
    // A failure (or an API without siteContent yet) just leaves the tab empty.
    if (!q) {
      setContent(undefined);
      return;
    }
    gqlQuery<SiteContentData>(config.api, SITE_CONTENT_QUERY, {
      market: [config.market],
      branchGroupId: config.branchGroupId,
      q,
      page: 1,
      perPage: pageSize(params),
    })
      .then((data) => {
        if (index !== requestIndex.current) return;
        const siteContent = data.search?.siteContent;
        setContent(siteContent ? toContentIndex(siteContent, params) : undefined);
      })
      .catch(() => {
        if (index === requestIndex.current) setContent(undefined);
      });
  }, [paramsKey, disabled, baseVariables]);

  const loadMoreCars = useCallback(async () => {
    if (!cars) return;
    const size = cars.documentList.pagination.hitsPerPage || pageSize(params);
    const page = Math.floor(cars.documentList.pagination.offset / size) + 2;
    const index = requestIndex.current;
    setIsLoadingMore(true);
    setIsError(false);
    try {
      const data = await gqlQuery<SiteSearchData>(config.api, SITE_SEARCH_QUERY, {
        ...baseVariables(page),
        withFacets: false,
      });
      // The filters changed while this page was loading: it belongs to the old list.
      if (index !== requestIndex.current) return;
      const ads = data.search?.ads;
      if (ads) {
        setCars((current) => ({
          ...toSearchIndex(ads, params, current?.documentList.documents),
          facets: current?.facets || null,
        }));
      }
    } catch (_e) {
      if (index === requestIndex.current) setIsError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [cars, baseVariables]);

  const loadMoreContent = useCallback(async () => {
    if (!content || !q) return;
    const size = content.documentList.pagination.hitsPerPage || pageSize(params);
    const page = Math.floor(content.documentList.pagination.offset / size) + 2;
    const index = requestIndex.current;
    setIsLoadingMore(true);
    setIsError(false);
    try {
      const data = await gqlQuery<SiteContentData>(config.api, SITE_CONTENT_QUERY, {
        market: [config.market],
        branchGroupId: config.branchGroupId,
        q,
        page,
        perPage: size,
      });
      if (index !== requestIndex.current) return;
      const siteContent = data.search?.siteContent;
      if (siteContent) {
        setContent((current) =>
          toContentIndex(siteContent, params, current?.documentList.documents)
        );
      }
    } catch (_e) {
      if (index === requestIndex.current) setIsError(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [content, q, config]);

  const carFacets = useMemo(
    () => (unfiltered ? toInitialFacets(unfiltered, params, config.market) : undefined),
    [unfiltered, paramsKey, config.market]
  );

  const result = useMemo<SiteSearchResult | undefined>(() => {
    if (!cars) return undefined;
    // The filtered result only carries list facets; the range facets (built
    // from the unfiltered histograms) are added so the panel counts them too.
    const rangeFacets = carFacets?.filter((f) => f.selectionType === 'range') || [];
    return {
      carFacets,
      cars: { ...cars, facets: [...(cars.facets || []), ...rangeFacets] },
      retailers_cms: content,
    };
  }, [cars, content, carFacets]);

  const hasMoreCars = !!cars?.documentList.pagination.nextPage;
  const hasMoreContent = !!content?.documentList.pagination.nextPage;
  const loadMore = useCallback(
    (searchType?: SearchType) => {
      if (searchType === 'retailers_cms') {
        loadMoreContent();
      } else {
        loadMoreCars();
      }
    },
    [loadMoreCars, loadMoreContent]
  );

  return [
    result,
    { isLoading, isLoadingMore, isSuccess, isError },
    hasMoreCars || hasMoreContent ? loadMore : undefined,
  ];
};

export default useSiteSearch;
